import React, { useState, useEffect } from "react";
import api from "../api";
import { toast } from "sonner";
import OrderForm from "../components/OrderForm";
import { ShoppingCart, Plus, Filter, CreditCard } from "lucide-react";
import { createCheckoutSession } from "../api";

const statusLabels = {
  pending: { label: "Oczekujace", color: "bg-amber-100 text-amber-700" },
  paid: { label: "Oplacone", color: "bg-green-100 text-green-700" },
  delivered: { label: "Dostarczone", color: "bg-blue-100 text-blue-700" },
  cancelled: { label: "Anulowane", color: "bg-red-100 text-red-700" },
};

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const loadOrders = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await api.get("/orders", { params });
      setOrders(res.data);
    } catch {
      toast.error("Blad ladowania zamowien");
    } finally {
      setLoading(false);
    }
  };

  const handleStripeCheckout = async (orderId) => {
    try {
      const res = await createCheckoutSession(orderId);
      if (res.checkout_url) {
        window.open(res.checkout_url, "_blank");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad tworzenia sesji platnosci");
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}`, { status: newStatus });
      loadOrders();
      toast.success("Status zaktualizowany");
    } catch {
      toast.error("Blad aktualizacji statusu");
    }
  };

  if (loading) return <div className="text-center py-20 text-slate-500">Ladowanie zamowien...</div>;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Zamowienia</h1>
          <p className="text-slate-500 text-sm">{orders.length} zamowien</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" /> Nowe zamowienie
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 mb-4 items-center">
        <Filter className="w-4 h-4 text-slate-400" />
        {["", "pending", "paid", "delivered", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              statusFilter === s
                ? "bg-indigo-600 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {s === "" ? "Wszystkie" : statusLabels[s]?.label || s}
          </button>
        ))}
      </div>

      {/* Orders list */}
      {orders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300">
          <ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Brak zamowien</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 text-left text-sm text-slate-500">
                <th className="px-4 py-3 font-medium">Nr</th>
                <th className="px-4 py-3 font-medium">Klient</th>
                <th className="px-4 py-3 font-medium">Pozycje</th>
                <th className="px-4 py-3 font-medium text-right">Kwota</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Data</th>
                <th className="px-4 py-3 font-medium">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const st = statusLabels[order.status] || statusLabels.pending;
                return (
                  <tr key={order.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-sm">{order.order_number}</td>
                    <td className="px-4 py-3 text-sm text-slate-900">{order.client_name || "-"}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{order.items?.length || 0} poz.</td>
                    <td className="px-4 py-3 text-sm font-medium text-right">{order.total_amount?.toFixed(2)} PLN</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(order.created_at).toLocaleDateString("pl-PL")}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <select
                          value={order.status}
                          onChange={(e) => updateStatus(order.id, e.target.value)}
                          className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          <option value="pending">Oczekujace</option>
                          <option value="paid">Oplacone</option>
                          <option value="delivered">Dostarczone</option>
                          <option value="cancelled">Anulowane</option>
                        </select>
                        {order.status === "pending" && (
                          <button
                            onClick={() => handleStripeCheckout(order.id)}
                            className="text-xs bg-indigo-50 text-indigo-600 px-2 py-1 rounded hover:bg-indigo-100 flex items-center gap-1"
                            title="Wyslij link do platnosci"
                          >
                            <CreditCard className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create order modal */}
      {showCreate && (
        <OrderForm
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            loadOrders();
          }}
        />
      )}
    </div>
  );
};

export default OrdersPage;
